import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiClock } from 'react-icons/fi';

import { apiService } from '../../components/Config/api.service';
import { notify } from '../../components/Utils/Notifications';

/**
 * L'horaire de travail d'un salarié, choisi parmi ceux de sa société
 * (Yros/Kavero#491). Un salarié en rotation n'a pas l'horaire des bureaux ;
 * c'est à lui qu'Odoo compare ses pointages. « Enregistrer » explicite : un
 * changement d'horaire recalcule ses heures supplémentaires.
 */
function HoraireSalarie({ employe }) {
  const { t } = useTranslation();
  const [donnees, setDonnees] = useState(null);
  const [choix, setChoix] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    // La fiche monte la carte avant d'avoir chargé le salarié.
    if (!employe?.id) return undefined;
    let actif = true;
    Promise.resolve()
      .then(() => apiService.lireHoraireSalarie(employe.id))
      .then((reponse) => {
        // Une réponse sans liste ne doit pas faire tomber toute la fiche.
        if (!actif || !reponse?.ok || !Array.isArray(reponse.horaires)) return;
        setDonnees(reponse);
        setChoix(reponse.horaire_id ? String(reponse.horaire_id) : '');
      })
      .catch(() => {});
    return () => { actif = false; };
  }, [employe?.id]);

  // Rien à régler, ou pas le droit de le voir : la carte se tait.
  if (!donnees) return null;

  const enregistrer = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      const reponse = await apiService.ecrireHoraireSalarie(employe.id, Number(choix));
      if (Array.isArray(reponse?.horaires)) setDonnees(reponse);
      notify(t('horaireSalarie.enregistre'), '', 2);
    } catch (e) {
      setErreur(e?.response?.data?.message || t('horaireSalarie.erreur'));
    } finally {
      setEnCours(false);
    }
  };

  const modifie = choix !== (donnees.horaire_id ? String(donnees.horaire_id) : '');
  return (
    <div className="py-6 px-3" data-testid="horaire-salarie">
      <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <FiClock className="text-[--color-primary]" /> {t('horaireSalarie.titre')}
      </h3>
      {!donnees.horaire_id && (
        <p className="mb-2 text-xs text-amber-700">{t('horaireSalarie.libre')}</p>
      )}
      <div className="flex items-center gap-2">
        <select data-testid="horaire-salarie-choix" value={choix} onChange={(e) => setChoix(e.target.value)}
          className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm">
          {!donnees.horaire_id && <option value="">{t('horaireSalarie.aucun')}</option>}
          {donnees.horaires.map((h) => (
            <option key={h.id} value={h.id}>
              {h.par_defaut ? t('horaireSalarie.parDefaut', { nom: h.nom }) : h.nom}
            </option>
          ))}
        </select>
        <button type="button" data-testid="horaire-salarie-enregistrer" onClick={enregistrer}
          disabled={!modifie || !choix || enCours}
          className="px-3 py-2 text-sm rounded-lg bg-[--color-primary] text-white hover:bg-[--bg-hover] disabled:opacity-50">
          {t('horaireSalarie.enregistrer')}
        </button>
      </div>
      {erreur && <p role="alert" className="mt-2 text-sm text-red-700">{erreur}</p>}
    </div>
  );
}

export default HoraireSalarie;
